import { Autocomplete, Grid, Stack, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import * as opcClientApi from '../../api/app/opcClientApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { CheckBox } from '../../components/core/CheckBox/CheckBox';
import { Loader } from '../../components/core/Loader/Loader';
import { SelectBoxAutocomplete } from '../../components/core/SelectBox/SelectBoxAutocomplete';
import { useTranslation } from '../../contexts/translation';
import { MonitoredCounterScrapInputs } from '../../types/app/monitoredCounterScrap.types';
import { OpcClient } from '../../types/app/opcClient.types';
import { dictionary } from '../../utils/core/dictionary';
interface Props {
  onSubmit: SubmitHandler<MonitoredCounterScrapInputs>;
  defaultValues?: MonitoredCounterScrapInputs;
  onBack: any;
  isUsedHydra: boolean;
}

export function MonitoredCounterScrapForm({ onSubmit, defaultValues, onBack, isUsedHydra }: Props) {
  const { t } = useTranslation();
  const [endpointAddress, setEndpointAddress] = useState<string>('');
  const [nodeList, setNodeList] = useState<string[]>();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm<MonitoredCounterScrapInputs>();

  const {
    data: opcClientList,
    isLoading,
    isError,
  } = useQuery<OpcClient[]>('opcClientList', opcClientApi.getOpcClientList);

  useQuery<string[]>(['nodeList', endpointAddress], () => opcClientApi.browse(endpointAddress), {
    enabled: endpointAddress.length > 0,
    onSuccess: (res) => {
      let tmpNodeList: string[] = [];
      res.map((r: string) => {
        tmpNodeList = [...tmpNodeList, r];
      });
      setNodeList(tmpNodeList);
    },
  });

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!defaultValues?.opcClientId && opcClientList) return;
    let tmpC = opcClientList?.find((x: any) => x.id === defaultValues?.opcClientId);
    if (tmpC) setEndpointAddress(tmpC.endpointAddress);
  }, [defaultValues && defaultValues?.opcClientId]);

  useEffect(() => {
    if (defaultValues) return;
    setValue('name', 'Scrap');
    setValue('influxValueName', 'sc00');
    setValue('nodeId', 'ns=2;s=');
  }, []);

  if (isError) return <ApiError />;
  if (isLoading || !opcClientList || isUsedHydra === undefined) return <Loader />;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Name"
              {...register('name', {
                required: `Name ${dictionary.IS_REQUIRED}`,
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectBoxAutocomplete
              isRequired
              label={'OPC client'}
              items={opcClientList}
              valueKey={'id'}
              labelKey={'instanceName'}
              onChange={(e: any) => {
                let tmpC = opcClientList.find((x: OpcClient) => x.id === e);
                if (tmpC) setEndpointAddress(tmpC?.endpointAddress);
              }}
              controllerProps={{
                name: 'opcClientId',
                control: control,
                rules: { required: 'OPC client ' + dictionary.IS_REQUIRED },
              }}
              errors={errors}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Influx value name"
              {...register('influxValueName', {
                required: `Influx value name ${dictionary.IS_REQUIRED}`,
              })}
            />
          </Grid>
          <Grid item xs={12}>
            {!nodeList && (
              <TextField
                fullWidth
                required
                label="nodeId"
                {...register('nodeId', { required: `Node id ${dictionary.IS_REQUIRED}` })}
              />
            )}
            {nodeList && (
              <Autocomplete
                id="free-solo-demo"
                freeSolo
                value={defaultValues && defaultValues.nodeId ? defaultValues.nodeId : 'ns=2;s='}
                options={nodeList.map((node) => node)}
                onInputChange={(e, data) => setValue('nodeId', data)}
                renderInput={(params) => <TextField {...params} label="nodeId" />}
              />
            )}
          </Grid>
          {isUsedHydra && (
            <Grid item xs={12}>
              <CheckBox
                label={'Use multiple with hydra partitioning'}
                controllerProps={{
                  name: 'useMultipleWithHydraPartitioning',
                  control: control,
                }}
                errors={errors}
              />
            </Grid>
          )}
          <Grid item>
            <Stack spacing={2} direction="row">
              <Button type="button" variant="outlined" onClick={onBack}>
                {t('general.action-buttons.back')}
              </Button>
              <Button type="submit" variant="contained" color="success">
                {t('general.action-buttons.save')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
